import { motion } from 'framer-motion';
import { Flower2, Heart, Truck, Store, Calendar, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const services = [
  {
    icon: Flower2,
    title: 'Bouquets & Compositions',
    description: 'Des créations uniques pour chaque occasion, réalisées avec des fleurs fraîches de saison.',
    image: 'https://images.unsplash.com/photo-1508610048659-a06b669e3321?w=600&q=80',
    span: 'md:col-span-4 lg:col-span-6',
  },
  {
    icon: Heart,
    title: 'Mariages',
    description: "Sublimez votre jour J avec nos créations florales sur mesure.",
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600&q=80',
    span: 'md:col-span-4 lg:col-span-6',
  },
  {
    icon: Calendar,
    title: 'Événements',
    description: "Décoration florale pour entreprises, réceptions et cérémonies.",
    image: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=600&q=80',
    span: 'md:col-span-4 lg:col-span-4',
  },
  {
    icon: Gift,
    title: 'Deuil & Hommages',
    description: 'Des arrangements respectueux pour honorer la mémoire de vos proches.',
    image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=600&q=80',
    span: 'md:col-span-4 lg:col-span-4',
  },
  {
    icon: Truck,
    title: 'Livraison',
    description: 'Livraison à Sion et environs. Vos fleurs arrivent fraîches et à temps.',
    span: 'md:col-span-4 lg:col-span-2',
    noImage: true,
  },
  {
    icon: Store,
    title: 'Retrait',
    description: 'Retrait en magasin disponible sur commande.',
    span: 'md:col-span-4 lg:col-span-2',
    noImage: true,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

export const ServicesSection = () => {
  return (
    <section
      data-testid="services-section"
      className="py-24 md:py-32 bg-secondary/30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-accent font-accent italic text-lg mb-4"
          >
            Notre savoir-faire
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-primary tracking-tight"
          >
            Nos Services
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-muted-foreground max-w-2xl mx-auto text-base md:text-lg"
          >
            De la composition artisanale à la livraison, nous accompagnons vos moments précieux avec passion et expertise.
          </motion.p>
        </div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={itemVariants}
              data-testid={`service-card-${index}`}
              className={`${service.span} group relative overflow-hidden rounded-3xl bg-white transition-all duration-500 hover:shadow-xl hover:-translate-y-1 border border-primary/5`}
            >
              {service.noImage ? (
                /* Small Card - No Image */
                <div className="p-8 h-full flex flex-col justify-between min-h-[200px]">
                  <div>
                    <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                      <service.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-primary mb-2">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {service.description}
                    </p>
                  </div>
                </div>
              ) : (
                /* Large Card - With Image */
                <div className="relative h-full min-h-[300px] md:min-h-[350px]">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-serif text-2xl font-semibold text-white mb-2">
                      {service.title}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {service.description}
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link to="/services" data-testid="services-more-btn">
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 py-6 text-lg font-medium border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all"
            >
              Découvrir tous nos services
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
